import { legal, type LegalPageKey } from '../content/legal';
import { pageSeo, type PageSeo } from '../content/seo';
import { Seo } from '../components/seo/Seo';
import { Section } from '../components/layout/Section';

/**
 * /privacy and /terms.
 *
 * The build agent does not write legal text (CLAUDE.md hard rule 2, spec
 * section 19). The route, the layout, and the seam are real; the copy is a
 * human's job. Until it arrives this page shouts, because a plausible-looking
 * placeholder is exactly the failure mode the rule exists to prevent.
 */
function LegalPage({ pageKey, seo }: { pageKey: LegalPageKey; seo: PageSeo }) {
  const page = legal.pages[pageKey];
  const hasCopy = page.supplied && (page.body !== undefined || page.sections !== undefined);

  return (
    <>
      <Seo seo={seo} />
      <Section>
        <h1 className="text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{page.title}</h1>
        {hasCopy && page.updated ? (
          <p className="mt-2 text-sm text-ink/70">{page.updated}</p>
        ) : null}

        {hasCopy ? (
          <div className="mt-8 max-w-3xl">
            {page.body?.map((paragraph) => (
              <p key={paragraph} className="mt-4 text-base leading-relaxed text-ink">
                {paragraph}
              </p>
            ))}
            {page.sections?.map((section) => (
              <section key={section.heading} className="mt-10">
                <h2 className="text-xl font-semibold text-ink">{section.heading}</h2>
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph} className="mt-4 text-base leading-relaxed text-ink">
                    {paragraph}
                  </p>
                ))}
                {section.list ? (
                  <ul className="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-ink">
                    {section.list.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ))}
          </div>
        ) : (
          <div className="mt-8 max-w-3xl">
            <div className="rounded-lg border-4 border-signal bg-signal p-6 text-field sm:p-8">
              <p className="text-xl font-bold uppercase leading-tight tracking-wide sm:text-2xl">
                {legal.banner}
              </p>
              <p className="mt-4 text-sm leading-relaxed sm:text-base">{legal.bannerNote}</p>
            </div>

            <h2 className="mt-10 text-lg font-semibold text-ink">{legal.requirementsHeading}</h2>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-base leading-relaxed text-ink">
              {page.requirements.map((requirement) => (
                <li key={requirement}>{requirement}</li>
              ))}
            </ul>

            <p className="mt-8 text-sm text-ink/75">{legal.sourceNote}</p>
          </div>
        )}
      </Section>
    </>
  );
}

export function PrivacyPage() {
  return <LegalPage pageKey="privacy" seo={pageSeo.privacy} />;
}

export function TermsPage() {
  return <LegalPage pageKey="terms" seo={pageSeo.terms} />;
}
